import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import BlogSidebar from "../BlogSidebar/BlogSidebar";
import NewsletterSignup from "../NewsletterSignup/NewsletterSignup";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "https://api.axoix.in";
const POSTS_PER_PAGE = 10;

interface ApiBlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string | null;
  image: string | null;
  author_name: string;
  category: { name: string; color: string } | null;
  tags: string[] | null;
  reading_time_mins: number | null;
  published_at: string | null;
  created_at: string | null;
}

const getImageUrl = (image: string | null): string | null => {
  if (!image) return null;
  if (image.startsWith("http")) return image;
  // Server-relative (e.g. /uploads/blog/9/featured-...webp) — resolve via
  // the axoix.com nginx alias when running on the marketing site, otherwise
  // prefix with api.axoix.in (which has the same alias).
  if (image.startsWith("/")) {
    if (typeof window !== "undefined" && window.location.host.endsWith("axoix.com")) {
      return image;
    }
    return `${API_BASE}${image}`;
  }
  // Bare filename → legacy GPU-generated image route
  return `${API_BASE}/api/chorus/blog/ai/image/${image}`;
};

const formatDate = (isoDate: string | null): string => {
  if (!isoDate) return "";
  const d = new Date(isoDate);
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
};

const Icons = {
  arrow: (
    <>
      <svg width="20" height="19" viewBox="0 0 20 19" fill="none">
        <rect x="3.783" y="13.463" width="14.31" height="1.817" transform="rotate(-40.2798 3.783 13.463)" fill="white" />
        <rect x="5.807" y="4.605" width="1.817" height="1.817" transform="rotate(-40.2798 5.807 4.605)" fill="white" />
        <rect x="8.367" y="4.816" width="1.817" height="1.817" transform="rotate(-40.2798 8.367 4.816)" fill="white" />
        <rect x="10.926" y="5.028" width="1.817" height="1.817" transform="rotate(-40.2798 10.926 5.028)" fill="white" />
        <rect x="13.277" y="7.8" width="1.817" height="1.817" transform="rotate(-40.2798 13.277 7.8)" fill="white" />
        <rect x="13.066" y="10.361" width="1.817" height="1.817" transform="rotate(-40.2798 13.066 10.361)" fill="white" />
        <rect x="12.855" y="12.922" width="1.817" height="1.817" transform="rotate(-40.2798 12.855 12.922)" fill="white" />
      </svg>
      <svg width="20" height="19" viewBox="0 0 20 19" fill="none">
        <rect x="3.783" y="13.463" width="14.31" height="1.817" transform="rotate(-40.2798 3.783 13.463)" fill="white" />
        <rect x="5.807" y="4.605" width="1.817" height="1.817" transform="rotate(-40.2798 5.807 4.605)" fill="white" />
        <rect x="8.367" y="4.816" width="1.817" height="1.817" transform="rotate(-40.2798 8.367 4.816)" fill="white" />
        <rect x="10.926" y="5.028" width="1.817" height="1.817" transform="rotate(-40.2798 10.926 5.028)" fill="white" />
        <rect x="13.277" y="7.8" width="1.817" height="1.817" transform="rotate(-40.2798 13.277 7.8)" fill="white" />
        <rect x="13.066" y="10.361" width="1.817" height="1.817" transform="rotate(-40.2798 13.066 10.361)" fill="white" />
        <rect x="12.855" y="12.922" width="1.817" height="1.817" transform="rotate(-40.2798 12.855 12.922)" fill="white" />
      </svg>
    </>
  ),
};

const imageStyles = `
  .blog-card-image {
    width: 100%;
    height: 220px;
    object-fit: cover;
    border-radius: 12px;
    margin-bottom: 20px;
    border: 1px solid rgba(0,255,151,0.15);
  }
  .blog-card-image-placeholder {
    width: 100%;
    height: 220px;
    border-radius: 12px;
    margin-bottom: 20px;
    background: linear-gradient(135deg, rgba(0,255,151,0.08), rgba(0,0,0,0.3));
    border: 1px solid rgba(0,255,151,0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 48px;
    color: rgba(0,255,151,0.3);
  }
  .blog-source-badge {
    display: inline-block;
    font-size: 10px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 10px;
    background: rgba(0,255,151,0.1);
    color: var(--color-primary);
    margin-left: 10px;
    vertical-align: middle;
  }
  .blog-pagination li.disabled a, .blog-pagination li.disabled button {
    opacity: 0.35;
    cursor: not-allowed;
    pointer-events: none;
  }
  .blog-pagination li.ellipsis a, .blog-pagination li.ellipsis span {
    background: transparent;
    box-shadow: none;
    backdrop-filter: none;
    cursor: default;
  }
  .blog-pagination li.ellipsis a:hover, .blog-pagination li.ellipsis span:hover {
    background: transparent;
    color: var(--color-white);
  }
`;

// Builds a compact page list with ellipsis gaps, e.g. [1, "...", 4, 5, 6, "...", 12]
const buildPageList = (current: number, total: number): (number | "...")[] => {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set<number>([1, total, current, current - 1, current + 1]);
  const sorted = Array.from(pages).filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const result: (number | "...")[] = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - (sorted[i - 1] as number) > 1) result.push("...");
    result.push(p);
  });
  return result;
};

const BlogList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get("page") || "1", 10);
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
  const query = searchParams.get("q") || "";

  const [apiPosts, setApiPosts] = useState<ApiBlogPost[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const offset = (page - 1) * POSTS_PER_PAGE;
    const searchQs = query ? `&search=${encodeURIComponent(query)}` : "";
    fetch(`${API_BASE}/api/chorus/blog/posts?site=axoix&status=published&limit=${POSTS_PER_PAGE}&offset=${offset}${searchQs}`)
      .then((res) => res.json())
      .then((data) => {
        setApiPosts(data.posts || []);
        setTotal(typeof data.total === "number" ? data.total : 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [page, query]);

  const totalPages = Math.max(1, Math.ceil(total / POSTS_PER_PAGE));
  const goToPage = (p: number) => {
    if (p < 1 || p > totalPages || p === page) return;
    const next = new URLSearchParams(searchParams);
    if (p === 1) next.delete("page"); else next.set("page", String(p));
    setSearchParams(next);
  };

  return (
    <div>
      <style>{imageStyles}</style>
      <section className="blog_details_section pt-120">
        <div className="container">
          <div className="row mt-none-50 g-0 align-items-start">
            <div className="col-lg-8 mt-50">
              <div className="blog_details_content">
                {query && (
                  <div className="mb-30" style={{ fontSize: 14, opacity: 0.75 }}>
                    Showing results for &ldquo;{query}&rdquo; ({total})
                    {" — "}
                    <Link to="/blog">Clear search</Link>
                  </div>
                )}
                {loading && (
                  <div className="text-center py-5 opacity-50">
                    <i className="fas fa-spinner fa-spin me-2"></i>Loading articles...
                  </div>
                )}

                {/* API-sourced posts (from CHORUS admin) */}
                {apiPosts.map((post, index) => {
                  const imgUrl = getImageUrl(post.image);
                  const tag = post.category?.name || (post.tags && post.tags[0]) || "AXOIX";
                  return (
                    <React.Fragment key={`api-${post.slug}`}>
                      <div className="blog_details_item xb-border">
                        <div className="xb-item--inner">
                          {imgUrl ? (
                            <Link to={`/blog/${post.slug}`}>
                              <img src={imgUrl} alt={post.title} className="blog-card-image" />
                            </Link>
                          ) : (
                            <div className="blog-card-image-placeholder">
                              <i className="fas fa-newspaper"></i>
                            </div>
                          )}
                          <div className="d-flex gap-3 mb-15 flex-wrap" style={{ fontSize: 13, opacity: 0.6 }}>
                            <span><i className="far fa-calendar me-1"></i>{formatDate(post.published_at || post.created_at)}</span>
                            {post.reading_time_mins && (
                              <span><i className="far fa-clock me-1"></i>{post.reading_time_mins} min read</span>
                            )}
                            <span><i className="far fa-user me-1"></i>{post.author_name}</span>
                          </div>
                          <Link to={`/blog/${post.slug}`} className="xb-item--tag">#{tag}</Link>
                          <h3 className="xb-item--title border-effect-2">
                            <Link to={`/blog/${post.slug}`}>{post.title}</Link>
                          </h3>
                          <span className="xb-item--content">{post.excerpt}</span>
                          <div className="xb-item--button mt-40">
                            <Link className="thm-btn agency-btn" to={`/blog/${post.slug}`}>
                              <span className="text">Read more</span>
                              <span className="arrow"><span className="arrow-icon">{Icons.arrow}</span></span>
                            </Link>
                          </div>
                        </div>
                      </div>
                      {index === 2 && (
                        <div className="blog_details_item xb-border">
                          <div className="xb-item--inner">
                            <NewsletterSignup source="blog_index" variant="card" />
                          </div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* No results message */}
                {!loading && apiPosts.length === 0 && (
                  <div className="text-center py-5 opacity-50">
                    {query ? `No articles found for "${query}".` : "No articles found. Check back soon!"}
                  </div>
                )}

                {!loading && total > POSTS_PER_PAGE && (
                  <ul className="blog-pagination">
                    {/* These are <button>, not <a>: they trigger JS and have no href,
                        so as anchors they were invisible to keyboard and screen-reader
                        users (jsx-a11y/anchor-is-valid). Styling is shared via the
                        `.blog-pagination li a, .blog-pagination li button` selector. */}
                    <li className={page <= 1 ? "disabled" : ""}>
                      <button type="button" onClick={() => goToPage(page - 1)} disabled={page <= 1} aria-label="Previous page">
                        <i className="fas fa-chevron-left"></i>
                      </button>
                    </li>
                    {buildPageList(page, totalPages).map((p, i) =>
                      p === "..." ? (
                        <li key={`ellipsis-${i}`} className="ellipsis"><span aria-hidden="true">&hellip;</span></li>
                      ) : (
                        <li key={p} className={p === page ? "active" : ""}>
                          <button
                            type="button"
                            onClick={() => goToPage(p)}
                            aria-label={`Page ${p}`}
                            aria-current={p === page ? "page" : undefined}
                          >
                            {p}
                          </button>
                        </li>
                      )
                    )}
                    <li className={page >= totalPages ? "disabled" : ""}>
                      <button type="button" onClick={() => goToPage(page + 1)} disabled={page >= totalPages} aria-label="Next page">
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                )}
              </div>
            </div>
            <BlogSidebar />
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogList;
